/**
 * Cancel Job Service
 * TypeScript implementation of the trapi-cli cancel job functionality
 */

interface CancelJobConfig {
  selectedApiPath: string;
  selectedApiVersion: string;
}

interface CancelJobResponse {
  success: boolean;
  message: string;
  alreadyTerminal?: boolean;
}

export class CancelJobService {
  private batchBaseUrl: string;
  private getToken: () => Promise<string>;
  private getUserOid: () => Promise<string>;

  constructor(
    batchBaseUrl: string, 
    getToken: () => Promise<string>,
    getUserOid: () => Promise<string>
  ) {
    this.batchBaseUrl = batchBaseUrl;
    this.getToken = getToken;
    this.getUserOid = getUserOid;
  }

  /**
   * Check the current status of a job before attempting to cancel
   */
  private async getJobStatus(jobId: string, config: CancelJobConfig): Promise<string | null> {
    try {
      const token = await this.getToken();
      const userOid = await this.getUserOid();
      
      if (!userOid) {
        throw new Error('User OID not available. Please ensure you are properly authenticated.');
      }

      const response = await fetch(
        `${this.batchBaseUrl}/batches/${jobId}?api-version=${config.selectedApiVersion}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-trapi-on-behalf-of': userOid,
            'oid': userOid,
            'x-trapi-api-path': config.selectedApiPath.startsWith('/') ? config.selectedApiPath : `/${config.selectedApiPath}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        console.warn(`Failed to get job status: ${response.status}`);
        return null;
      }

      const data = await response.json();
      return data.status || null;
    } catch (error) {
      console.warn('Error getting job status:', error);
      return null;
    }
  }

  /**
   * Check if a job status is terminal (cannot be cancelled)
   */
  private isTerminalStatus(status: string): boolean {
    const normalizedStatus = status.toLowerCase();
    return ['completed', 'failed', 'cancelled'].includes(normalizedStatus);
  }

  /**
   * Cancel a batch job
   */
  async cancelJob(jobId: string, config: CancelJobConfig): Promise<CancelJobResponse> {
    try {
      if (!jobId) {
        return {
          success: false,
          message: 'Job ID is required'
        };
      }

      const userOid = await this.getUserOid();
      
      if (!userOid) {
        return {
          success: false,
          message: 'User OID not available. Please ensure you are properly authenticated.'
        };
      }

      // First, check if the job is already in a terminal state
      const currentStatus = await this.getJobStatus(jobId, config);
      if (currentStatus && this.isTerminalStatus(currentStatus)) {
        return {
          success: true,
          message: `Job ${jobId} already terminal (${currentStatus}).`,
          alreadyTerminal: true
        };
      }

      // Attempt to cancel the job
      const token = await this.getToken();
      
      const response = await fetch(
        `${this.batchBaseUrl}/batches/${jobId}/cancel?api-version=${config.selectedApiVersion}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-trapi-on-behalf-of': userOid,
            'oid': userOid,
            'x-trapi-api-path': config.selectedApiPath.startsWith('/') ? config.selectedApiPath : `/${config.selectedApiPath}`,
            'Content-Length': '0'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `Cancel failed (${response.status}): ${errorText}`
        };
      }

      return {
        success: true,
        message: `Cancellation requested for job ${jobId}.`
      };

    } catch (error) {
      return {
        success: false,
        message: `Error cancelling job: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }
}

/**
 * Factory function to create a CancelJobService instance
 */
export function createCancelJobService(
  batchBaseUrl: string, 
  getToken: () => Promise<string>,
  getUserOid: () => Promise<string>
): CancelJobService {
  return new CancelJobService(batchBaseUrl, getToken, getUserOid);
}

/**
 * Utility function for direct job cancellation (compatible with existing usage)
 */
export async function cancelBatchJob(
  jobId: string,
  config: CancelJobConfig,
  batchBaseUrl: string,
  getToken: () => Promise<string>,
  getUserOid: () => Promise<string>
): Promise<CancelJobResponse> {
  const service = createCancelJobService(batchBaseUrl, getToken, getUserOid);
  return service.cancelJob(jobId, config);
}